import React from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import BuildIcon from '@mui/icons-material/Build';
import ScienceIcon from '@mui/icons-material/Science';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import SettingsIcon from '@mui/icons-material/Settings';
import CustomButton from './common/CustomButton';

const SectionWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: '80px 0',
  background: '#FFFFFF',
  position: 'relative',
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 0,
  alignItems: 'center',
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: '2rem',
  }
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '500px',
  position: 'relative',
  borderRadius: '8px',
  overflow: 'hidden',
  marginRight: '-5%',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  [theme.breakpoints.down('md')]: {
    height: '300px',
    marginRight: 0,
  }
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: '2.5rem',
  background: '#FFFFFF',
  borderRadius: '8px',
  border: '1px solid #E5E7EB',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  position: 'relative',
  zIndex: 2,
  marginLeft: '-5%',
  [theme.breakpoints.down('md')]: {
    marginLeft: 0,
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(2rem, 4vw, 2.5rem)',
  fontWeight: 700,
  color: '#1A365D',
  marginBottom: '1rem',
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  color: '#1A365D',
  marginBottom: '0.5rem',
  fontWeight: 500,
}));

const Description = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#475569',
  marginBottom: '2rem',
  lineHeight: 1.6,
}));

const ServicesTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  color: '#1A365D',
  marginBottom: '1.5rem',
  fontWeight: 500,
}));

const ServicesList = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '1rem',
  marginBottom: '2rem',
}));

const ServiceItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
}));

const IconBox = styled(Box)(({ theme }) => ({
  width: '48px',
  height: '48px',
  backgroundColor: '#73c043',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    fontSize: '24px',
    color: '#FFFFFF',
  }
}));

const services = [
  {
    icon: <BuildIcon />,
    title: 'Étalonnage',
  },
  {
    icon: <ScienceIcon />,
    title: 'Essais',
  }
];

const CTASection = () => {
  const navigate = useNavigate();

  return (
    <SectionWrapper>
      <Container 
        maxWidth="xl"
        sx={{
          px: { xs: 2, sm: 4, md: 8, lg: 12 },
        }}
      >
        <ContentWrapper>
          <ImageContainer>
            <img 
              src="/assets/images/CTA/22.jpg"
              alt="Laboratoire de métrologie avec équipements de précision"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://placehold.co/600x400?text=Laboratoire+de+métrologie';
              }}
            />
          </ImageContainer>

          <ContentContainer>
            <Title>
              Services de métrologie
            </Title>
            <Subtitle>
              Professionnels uniquement
            </Subtitle>
            <Description>
              Découvrez notre gamme complète de services de métrologie de haute précision, conçue spécifiquement pour répondre aux besoins des professionnels de l'industrie.
            </Description>

            <ServicesTitle>
              Services disponibles
            </ServicesTitle>
            <ServicesList>
              {services.map((service, index) => (
                <ServiceItem key={index}>
                  <IconBox>
                    {service.icon}
                  </IconBox>
                  <Typography sx={{ color: '#1A365D', fontWeight: 500 }}>
                    {service.title}
                  </Typography>
                </ServiceItem>
              ))}
            </ServicesList>

            <CustomButton
              fullWidth
              onClick={() => navigate('/services')}
            >
              Contactez nos équipes
            </CustomButton>
          </ContentContainer>
        </ContentWrapper>
      </Container>
    </SectionWrapper>
  );
};

export default CTASection;
